import { useState, useMemo, useEffect } from "react";

const FILTER_CSS = `
.td-fbar{
  display:flex;flex-wrap:wrap;gap:6px;align-items:center;
  padding:10px 12px;
  background:#f7f5f0;
  border:1px solid rgba(30,40,34,.08);
  border-radius:10px;
  margin-bottom:12px;
}
.td-fbar-label{
  font-size:9px;font-weight:800;text-transform:uppercase;
  letter-spacing:.11em;color:#7a8a82;white-space:nowrap;margin-right:2px;
  font-family:'IBM Plex Mono',monospace;
}
.td-fsel{
  appearance:none;-webkit-appearance:none;
  background:#fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%237a8a82'/%3E%3C/svg%3E") no-repeat right 7px center;
  border:1px solid rgba(30,40,34,.12);
  border-radius:8px;
  padding:5px 24px 5px 9px;
  font-size:11px;font-weight:600;
  color:#18221e;
  font-family:'IBM Plex Mono',monospace;
  cursor:pointer;outline:none;
  transition:all .15s;
  white-space:nowrap;
  max-width:100%;
}
.td-fsel:hover{border-color:rgba(181,154,91,.5);background:#fffdf7;}
.td-fsel.active{border-color:#b59a5b;color:#b59a5b;background:#fffaed;font-weight:800;}
.td-fsearch-wrap{position:relative;flex:1;min-width:120px;max-width:200px;}
.td-fsearch-wrap .td-fsearch-icon{
  position:absolute;left:8px;top:50%;transform:translateY(-50%);
  color:#a0a89e;font-size:11px;pointer-events:none;
}
.td-fsearch{
  width:100%;
  background:#fff;
  border:1px solid rgba(30,40,34,.12);
  border-radius:8px;padding:5px 9px 5px 26px;
  font-size:11px;color:#18221e;
  font-family:'IBM Plex Mono',monospace;
  outline:none;transition:all .15s;
}
.td-fsearch:focus{border-color:rgba(181,154,91,.5);background:#fffdf7;}
.td-fsearch.has-value{border-color:#b59a5b;background:#fffaed;}
.td-fsearch::placeholder{color:#a0a89e;}
.td-freset{
  border:1px solid rgba(181,154,91,.35);
  border-radius:7px;background:#fffaed;color:#b59a5b;
  font-size:10px;font-weight:800;cursor:pointer;
  font-family:'IBM Plex Mono',monospace;
  padding:4px 10px;
}
.td-fcount{
  font-size:10px;color:#7a8a82;
  font-family:'IBM Plex Mono',monospace;
  margin-left:auto;
}
@media(max-width:720px){
  .td-fbar{gap:5px;padding:8px 10px;}
  .td-fsearch-wrap{min-width:100%;max-width:100%;flex-basis:100%;}
  .td-fsel{flex:1;min-width:calc(50% - 4px);}
  .td-fcount{width:100%;margin-left:0;text-align:right;}
}
`;

export function useDrillFilterStyles() {
  useEffect(() => {
    let el = document.getElementById("td-drill-filters-css");
    if (!el) {
      el = document.createElement("style");
      el.id = "td-drill-filters-css";
      document.head.appendChild(el);
    }
    el.textContent = FILTER_CSS;
  }, []);
}

function normVal(v) {
  if (v == null || v === "") return "";
  return String(v).trim();
}

export function useDrillAssetFilters(rows, scopeKey) {
  const [search, setSearch] = useState("");
  const [fRegion, setFRegion] = useState(null);
  const [fCountry, setFCountry] = useState(null);
  const [fSector, setFSector] = useState(null);
  const [fStyle, setFStyle] = useState(null);
  const [fCap, setFCap] = useState(null);
  const [fFund, setFFund] = useState(null);

  const clearAll = () => {
    setSearch("");
    setFRegion(null);
    setFCountry(null);
    setFSector(null);
    setFStyle(null);
    setFCap(null);
    setFFund(null);
  };

  useEffect(() => {
    setSearch("");
    setFRegion(null);
    setFCountry(null);
    setFSector(null);
    setFStyle(null);
    setFCap(null);
    setFFund(null);
  }, [scopeKey]);

  const options = useMemo(() => {
    const list = rows || [];
    const uniq = (fn) =>
      [...new Set(list.map(fn).map(normVal).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b)
      );
    return {
      regions: uniq((r) => r.region),
      countries: uniq((r) => r.country),
      sectors: uniq((r) => r.sector),
      styles: uniq((r) => r.style),
      caps: uniq((r) => r.marketCap),
      fundamentals: uniq((r) => r.fundamental),
    };
  }, [rows]);

  const filtered = useMemo(() => {
    let r = [...(rows || [])];
    if (fRegion) r = r.filter((x) => normVal(x.region) === fRegion);
    if (fCountry) r = r.filter((x) => normVal(x.country) === fCountry);
    if (fSector) r = r.filter((x) => normVal(x.sector) === fSector);
    if (fStyle) r = r.filter((x) => normVal(x.style) === fStyle);
    if (fCap) r = r.filter((x) => normVal(x.marketCap) === fCap);
    if (fFund) r = r.filter((x) => normVal(x.fundamental) === fFund);
    if (search) {
      const q = search.toLowerCase();
      r = r.filter((x) => {
        const hay = [
          x.asset,
          x.region,
          x.country,
          x.sector,
          x.style,
          x.marketCap,
          x.fundamental,
        ]
          .map(normVal)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });
    }
    return r;
  }, [rows, search, fRegion, fCountry, fSector, fStyle, fCap, fFund]);

  const hasFilter =
    !!search || fRegion || fCountry || fSector || fStyle || fCap || fFund;

  return {
    filtered,
    search,
    setSearch,
    fRegion,
    setFRegion,
    fCountry,
    setFCountry,
    fSector,
    setFSector,
    fStyle,
    setFStyle,
    fCap,
    setFCap,
    fFund,
    setFFund,
    options,
    clearAll,
    hasFilter,
    total: (rows || []).length,
  };
}

export function DrillDownFilterBar({
  t,
  translateData,
  filter,
  labels = {},
}) {
  const L = {
    filters: labels.filters || t?.("filters") || "Filtres",
    search: labels.search || t?.("searchAsset") || "Symbole…",
    region: labels.region || t?.("regions") || "Région",
    country: labels.country || t?.("countries") || "Marché",
    sector: labels.sector || t?.("sectors") || "Secteur",
    style: labels.style || t?.("styles") || "Style",
    cap: labels.cap || t?.("marketCap") || "Cap",
    fundamental: labels.fundamental || t?.("fundamentalScore") || "Fondamental",
    reset: labels.reset || t?.("resetFilters") || "↺ Reset",
  };

  const {
    search,
    setSearch,
    fRegion,
    setFRegion,
    fCountry,
    setFCountry,
    fSector,
    setFSector,
    fStyle,
    setFStyle,
    fCap,
    setFCap,
    fFund,
    setFFund,
    options,
    clearAll,
    hasFilter,
    filtered,
    total,
  } = filter;

  const label = (v) => (translateData && v ? translateData(v) : v);

  return (
    <div className="td-fbar">
      <span className="td-fbar-label">{L.filters}</span>
      <div className="td-fsearch-wrap">
        <span className="td-fsearch-icon">⌕</span>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={L.search}
          className={`td-fsearch${search ? " has-value" : ""}`}
        />
      </div>
      <select
        value={fRegion || ""}
        onChange={(e) => setFRegion(e.target.value || null)}
        className={`td-fsel${fRegion ? " active" : ""}`}
      >
        <option value="">{L.region}</option>
        {options.regions.map((r) => (
          <option key={r} value={r}>
            {label(r)}
          </option>
        ))}
      </select>
      <select
        value={fCountry || ""}
        onChange={(e) => setFCountry(e.target.value || null)}
        className={`td-fsel${fCountry ? " active" : ""}`}
      >
        <option value="">{L.country}</option>
        {options.countries.map((r) => (
          <option key={r} value={r}>
            {label(r)}
          </option>
        ))}
      </select>
      <select
        value={fSector || ""}
        onChange={(e) => setFSector(e.target.value || null)}
        className={`td-fsel${fSector ? " active" : ""}`}
      >
        <option value="">{L.sector}</option>
        {options.sectors.map((r) => (
          <option key={r} value={r}>
            {label(r)}
          </option>
        ))}
      </select>
      <select
        value={fStyle || ""}
        onChange={(e) => setFStyle(e.target.value || null)}
        className={`td-fsel${fStyle ? " active" : ""}`}
      >
        <option value="">{L.style}</option>
        {options.styles.map((r) => (
          <option key={r} value={r}>
            {label(r)}
          </option>
        ))}
      </select>
      <select
        value={fCap || ""}
        onChange={(e) => setFCap(e.target.value || null)}
        className={`td-fsel${fCap ? " active" : ""}`}
      >
        <option value="">{L.cap}</option>
        {options.caps.map((r) => (
          <option key={r} value={r}>
            {label(r)}
          </option>
        ))}
      </select>
      <select
        value={fFund || ""}
        onChange={(e) => setFFund(e.target.value || null)}
        className={`td-fsel${fFund ? " active" : ""}`}
      >
        <option value="">{L.fundamental}</option>
        {options.fundamentals.map((r) => (
          <option key={r} value={r}>
            {label(r)}
          </option>
        ))}
      </select>
      {hasFilter && (
        <span className="td-fcount">
          {filtered.length} / {total}
        </span>
      )}
      {hasFilter && (
        <button type="button" className="td-freset" onClick={clearAll}>
          {L.reset}
        </button>
      )}
    </div>
  );
}
